import { useRef, useMemo, useState, useEffect } from 'react';

import clsx from 'clsx';

import {
  DefineColumn,
  Order,
  CompareFunction,
  SearchParams,
} from 'components/DataTable/types';
import { PaginationReturn } from 'generated';

import { CellRender } from './components';
import { useStyles } from './styles';

interface HeaderProps<T = any> {
  defineColumns: DefineColumn[];
  order: Order;
  onSearch(value: string, searchParam: keyof T): void;
  orderBy: keyof T | null;
  loading?: boolean;
  expansible?: boolean;
  searchParams: SearchParams<T>;
  onSort(property: keyof T, compareFunction?: CompareFunction): void;
  loadingColumnsQuantity?: number;
  fixedHeader?: boolean;
  pagination?: PaginationReturn;
}

const expansibleWidth = 70;
const fakeDefineColumn = {
  label: 'loading...',
  sortable: true,
};

export function Head<T = any>({
  defineColumns,
  order,
  orderBy,
  onSort,
  loading,
  expansible,
  searchParams,
  onSearch,
  loadingColumnsQuantity,
  pagination,
  fixedHeader = false,
}: HeaderProps<T>): JSX.Element {
  const classes = useStyles();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [rootWidth, setRootWidth] = useState<number>(0);

  const memoLoadingColumnsQuantity: DefineColumn[] = useMemo(() => {
    if (loadingColumnsQuantity !== undefined) {
      return Array(loadingColumnsQuantity)
        .fill(0)
        .map((_, index) => ({
          ...fakeDefineColumn,
          id: String(index),
        }));
    }

    if (!defineColumns.length) {
      return Array(loadingColumnsQuantity ?? 2)
        .fill(0)
        .map((_, index) => ({
          ...fakeDefineColumn,
          id: String(index),
        }));
    }

    return defineColumns;
  }, [loadingColumnsQuantity, defineColumns]);

  const memoGridTemplateColumns = useMemo(() => {
    if (loading) {
      return memoLoadingColumnsQuantity.map((_, index) =>
        index === 0 ? '200px' : '1fr',
      );
    }

    const templateColumns: string[] = defineColumns.map(column => {
      const { minWidth, maxWidth } = column;
      if (minWidth !== undefined && maxWidth !== undefined) {
        return `minmax(${minWidth}px, ${maxWidth}px)`;
      }

      if (minWidth !== undefined && maxWidth === undefined) {
        return `minmax(${minWidth}px, 1fr)`;
      }

      if (minWidth === undefined && maxWidth !== undefined) {
        return `minmax(220px, ${maxWidth}px)`;
      }

      return 'minmax(220px, 1fr)';
    });

    return templateColumns;
  }, [defineColumns, loading, memoLoadingColumnsQuantity]);

  useEffect(() => {
    if (rootRef.current) {
      setRootWidth(rootRef.current.offsetWidth);
    }
  }, []);

  return (
    <div
      className={clsx(classes.root, {
        [classes.fixedHeader]: fixedHeader,
      })}
      ref={rootRef}
      style={{ marginLeft: loading ? 0 : undefined }}
    >
      <div
        className={classes.grid}
        style={{
          gridTemplateColumns: `${memoGridTemplateColumns.join(' ')} ${
            expansible ? `${expansibleWidth}px` : ''
          }`,
        }}
      >
        {loading &&
          memoLoadingColumnsQuantity.map(column => (
            <CellRender<T>
              pagination={pagination}
              searchParams={searchParams}
              key={column.id}
              loading={loading}
              minWidth={column.minWidth}
              maxWidth={
                column.maxWidth ??
                `calc((${rootWidth}px - ${
                  expansible ? `${expansibleWidth}px` : '0px'
                }) / ${defineColumns.length})`
              }
              hasSeparator={false}
              order={order}
              orderBy={orderBy}
              defineColumn={column}
              onSearch={onSearch}
              onSort={onSort}
            />
          ))}
        {!loading &&
          defineColumns.map((column, index) => (
            <CellRender<T>
              pagination={pagination}
              searchParams={searchParams}
              key={column.id}
              loading={loading}
              minWidth={column.minWidth}
              maxWidth={
                column.maxWidth ??
                `calc((${rootWidth}px - ${
                  expansible ? `${expansibleWidth}px` : '0px'
                }) / ${defineColumns.length})`
              }
              hasSeparator={index !== 0}
              order={order}
              orderBy={orderBy}
              defineColumn={column}
              onSearch={onSearch}
              onSort={onSort}
            />
          ))}
        {expansible && <div style={{ width: expansibleWidth }} />}
      </div>
      {fixedHeader && <div className={classes.aggregateFixedHeader} />}
    </div>
  );
}
